import React, { useEffect } from 'react';
import './MainMenu.css';

const MainMenu = ({ onQuizSelect }) => {
  // Ensure component is properly mounted and visible
  useEffect(() => {
    console.log('MainMenu component mounted');
    // Ensure the component is visible and scrolled to top
    window.scrollTo(0, 0);
  }, []);

  const quizOptions = [
    // Core Training (Priority)
    {
      id: 'core-cocktails',
      title: 'Core Cocktails Drill',
      description: 'Intensive practice for essential cocktails you make every shift',
      icon: '🎯',
      difficulty: 'Hard',
      type: 'study'
    },
    // Quiz Modes
    {
      id: 'description-matching',
      title: 'Description Matching',
      description: 'Match menu items to their descriptions',
      icon: '🍽️',
      difficulty: 'Medium',
      type: 'quiz'
    },
    {
      id: 'build-description',
      title: 'Build the Description',
      description: 'Construct descriptions from ingredient lists',
      icon: '🔨',
      difficulty: 'Hard',
      type: 'quiz'
    },
    {
      id: 'wine-pairing',
      title: 'Wine Pairing Expert',
      description: 'Master wine and food pairings like a sommelier',
      icon: '🍷',
      difficulty: 'Expert',
      type: 'quiz'
    },
    {
      id: 'nutritional-info',
      title: 'Nutritional & Dietary',
      description: 'Learn allergens, modifications, and health benefits',
      icon: '🥗',
      difficulty: 'Medium',
      type: 'quiz'
    },
    {
      id: 'drinks-only',
      title: 'Drinks Quiz',
      description: 'Focus on cocktails and beverages',
      icon: '🍹',
      difficulty: 'Easy',
      type: 'quiz'
    },
    {
      id: 'brunch-only',
      title: 'Brunch Quiz',
      description: 'Test your brunch menu knowledge',
      icon: '🥞',
      difficulty: 'Easy',
      type: 'quiz'
    },
    {
      id: 'dinner-only',
      title: 'Dinner Quiz',
      description: 'Master the dinner entrees',
      icon: '🥩',
      difficulty: 'Easy',
      type: 'quiz'
    },
    {
      id: 'culinary-terms',
      title: 'Culinary Terms',
      description: 'Define ingredients and cooking techniques',
      icon: '📚',
      difficulty: 'Expert',
      type: 'quiz'
    },
    // Study Modes
    {
      id: 'flashcards',
      title: 'Flashcard Study',
      description: 'Quick review with flashcards by category',
      icon: '🗂️',
      difficulty: 'Easy',
      type: 'study'
    },
    {
      id: 'cocktail-flashcards',
      title: 'Cocktail Flashcards',
      description: 'Master classic cocktail recipes and techniques',
      icon: '🍸',
      difficulty: 'Medium',
      type: 'study'
    },
    {
      id: 'study-guide',
      title: 'Study Guides',
      description: 'Printable reference sheets for each category',
      icon: '📋',
      difficulty: 'Easy',
      type: 'study'
    },
    {
      id: 'audio-study',
      title: 'Audio Study Mode',
      description: 'Listen to explanations while multitasking',
      icon: '🎧',
      difficulty: 'Easy',
      type: 'study'
    },
    // Real-World Training
    {
      id: 'table-scenarios',
      title: 'Table Scenarios',
      description: 'Practice real guest interactions and situations',
      icon: '👥',
      difficulty: 'Hard',
      type: 'scenario'
    },
    {
      id: 'upselling-practice',
      title: 'Upselling Mastery',
      description: 'Learn to suggest wines, appetizers, and upgrades',
      icon: '💰',
      difficulty: 'Medium',
      type: 'scenario'
    },
    {
      id: 'complaint-handling',
      title: 'Complaint Resolution',
      description: 'Handle difficult situations with confidence',
      icon: '🛠️',
      difficulty: 'Hard',
      type: 'scenario'
    },
    {
      id: 'vip-service',
      title: 'VIP Service Training',
      description: 'Elevate service for special occasions and VIPs',
      icon: '⭐',
      difficulty: 'Expert',
      type: 'scenario'
    }
  ];

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return '#28a745';
      case 'Medium': return '#ffc107';
      case 'Hard': return '#fd7e14';
      case 'Expert': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'quiz': return '#8B0000';
      case 'study': return '#17a2b8';
      case 'scenario': return '#6f42c1';
      default: return '#6c757d';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'quiz': return 'Quiz';
      case 'study': return 'Study';
      case 'scenario': return 'Practice';
      default: return 'Mode';
    }
  };

  // Group options by type
  const quizModes = quizOptions.filter(option => option.type === 'quiz');
  const studyModes = quizOptions.filter(option => option.type === 'study');
  const scenarioModes = quizOptions.filter(option => option.type === 'scenario');

  const renderSection = (title, options, description) => (
    <div className="menu-section">
      <div className="section-header">
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <div className="quiz-grid">
        {options.map((option) => (
          <div 
            key={option.id} 
            className="quiz-card"
            onClick={() => onQuizSelect(option.id)}
          >
            <div className="quiz-icon">{option.icon}</div>
            <h4>{option.title}</h4>
            <p>{option.description}</p>
            <div className="card-badges">
              <div 
                className="difficulty-badge"
                style={{ backgroundColor: getDifficultyColor(option.difficulty) }}
              >
                {option.difficulty}
              </div>
              <div 
                className="type-badge"
                style={{ backgroundColor: getTypeColor(option.type) }}
              >
                {getTypeLabel(option.type)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="main-menu">
      <div className="menu-header">
        <h2>Red Tavern Training Center</h2>
        <p>Master your menu knowledge and become the server your tables deserve</p>
      </div>
      
      {renderSection(
        "📝 Knowledge Quizzes", 
        quizModes, 
        "Test your knowledge with interactive quizzes"
      )}
      
      {renderSection(
        "📚 Study Modes", 
        studyModes, 
        "Quick review and reference materials"
      )}
      
      {renderSection(
        "🎭 Real-World Practice", 
        scenarioModes, 
        "Practice actual guest interactions and service situations"
      )}
      
      <div className="menu-footer">
        <p>💡 Master these skills to provide exceptional service and make every table feel special!</p>
      </div>
    </div>
  );
};

export default MainMenu; 